// pipe: 4B.6 (D8) — the SHARED lifecycle-advance affordance.
//
// Import discipline (load-bearing, mirrors records-view.tsx): this file imports ONLY
// @affine/component (Button) + @affine/i18n (useI18n) + React + its own css module — NOT the
// runtime ErpService, DI, workbench, or Electron — so it renders cleanly under happy-dom and is
// reusable by the Records grid (now) and the stage Worklist (Task 9) with zero clone-paste.
import { Button } from '@affine/component';
import { humanizeToken } from '@affine/erp-ui';
import { useI18n } from '@affine/i18n';
import { useState } from 'react';

import * as styles from './transition-affordance.css';

/**
 * 4B.6 (D8): the lifecycle-advance affordance, driven STRICTLY by the SERVER's `allowedTransitions`
 * (the legal next-states the current actor may advance to — already stage-gated server-side). It is
 * a PURE presenter: it never re-derives FSM edges (`lc.edges`) or re-checks roles — closing the
 * `[REVIEW-FIX]` gap where a button could show even where the server would 403. Shared by the
 * Records grid and the stage Worklist (SSOT/DRY). A rejected advance surfaces the server guard
 * message inline (no top-level alert).
 */
export function TransitionAffordance(props: {
  allowedTransitions: string[];
  onAdvance?: (to: string) => Promise<unknown>;
}) {
  const t = useI18n();
  const [error, setError] = useState<string | null>(null);
  const next = props.allowedTransitions ?? [];
  if (!props.onAdvance || next.length === 0) {
    return error ? (
      <div className={styles.transitionError} role="alert">
        {error}
      </div>
    ) : null;
  }
  return (
    <>
      {next.map(to => {
        // Display the human-readable target state; the RAW `to` token is what advances the lifecycle.
        const label = t['com.affine.pipe.records.transition.advance-to']({
          state: humanizeToken(to),
        });
        return (
          <Button
            key={to}
            variant="plain"
            size="default"
            className={styles.transitionButton}
            aria-label={label}
            onClick={() => {
              setError(null);
              props.onAdvance!(to).catch((e: unknown) =>
                setError(
                  e instanceof Error
                    ? e.message
                    : t['com.affine.pipe.records.transition.failed']()
                )
              );
            }}
          >
            {label}
          </Button>
        );
      })}
      {error && (
        <div className={styles.transitionError} role="alert">
          {error}
        </div>
      )}
    </>
  );
}
